/**
 * @fileoverview Authentication context and hook for Server-Side Google OAuth.
 * Provides application-wide authentication state management.
 * Handles login via redirect, token persistence in localStorage,
 * and session restoration.
 * @module hooks/useAuth
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { User } from "@shared/schema";
import { API_BASE_URL } from "@/config";

/**
 * Authentication context type definition.
 * Provides user state, loading status, and auth methods.
 */
interface AuthContextType {
  /** Current authenticated user, null if not logged in */
  user: User | null;
  /** Whether auth initialization is in progress */
  isLoading: boolean;
  /** Whether user is currently authenticated */
  isAuthenticated: boolean;
  /** JWT token for API requests */
  token: string | null;
  /** Triggers Google OAuth login flow via redirect */
  login: () => void;
  /** Clears auth state and token */
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Authentication provider component.
 * Wraps the application to provide auth context. Handles:
 * - Session restoration from localStorage
 * - Token parsing from URL query params (OAuth callback)
 * - Token validation and user state management
 * @param props - Component props
 * @param props.children - Child components
 * @returns Provider component wrapping children
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for token in URL (callback from backend)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");

    if (urlToken) {
      handleToken(urlToken);
      // Clear token from URL to prevent leaking it in history
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Check localStorage
      const savedToken = localStorage.getItem("authToken");
      if (savedToken) {
        handleToken(savedToken);
      } else {
        setIsLoading(false);
      }
    }
  }, []);

  const handleToken = (jwtToken: string) => {
    try {
      // Decode JWT payload (base64url)
      // Replace Base64URL characters with Base64 characters and add padding if needed
      const base64Url = jwtToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);

      // Check expiration
      if (payload.exp * 1000 > Date.now()) {
        const userData: User = {
          id: payload.sub,
          email: payload.email,
          firstName: payload.firstName || "",
          lastName: payload.lastName || "",
          profileImageUrl: payload.picture || null,
          createdAt: new Date(), // These might need to be fetched if critical
          updatedAt: new Date(),
        };

        setUser(userData);
        setToken(jwtToken);
        localStorage.setItem("authToken", jwtToken);
      } else {
        console.warn("Token expired");
        logout();
      }
    } catch (error) {
      console.error("Failed to parse token:", error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initiates Google OAuth login flow.
   * Redirects the browser to the backend auth endpoint.
   */
  const login = () => {
    // Use window.location.href to redirect to backend auth route
    // Ensure API_BASE_URL doesn't have trailing slash if we add one, or vice versa
    const baseUrl = API_BASE_URL.replace(/\/$/, "");
    window.location.href = `${baseUrl}/api/auth/google`;
  };

  /**
   * Logs out the current user.
   * Clears user state, token, and localStorage.
   */
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    console.log("Logged out");
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      token,
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication context.
 * Must be used within an AuthProvider.
 * @returns Authentication context with user state and methods
 * @throws Error if used outside AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
